import React, { useState, useEffect, useRef } from "react";
import { Formik } from "formik";
import {
  getMerchantFilterBounds,
  SearchOrganizations,
  getEmsOffices,
  getUserBranchesList,
  getEmsRepCodes,
} from "../../../../../api";
import { useSavedFilters } from "../../../../hooks/useSavedFilters";
import { usePaginatedOfficeOptions } from "../../../../hooks/usePaginatedOfficeOptions";
import SavedFiltersSection from "../../../../components/SavedFiltersSection/SavedFiltersSection";
import Drawer from "react-modern-drawer";
import standardCurrencyFormatter from "../../../../utils/standard-currency-formatter";
import { filterSchema } from "./validation-schema";
import ButtonIcon from "../../../../components/ButtonIcon/ButtonIcon";
import CloseIcon24 from "../../../../assets/svg/icons/common/CloseIcon24";
import Calender from "../../../../assets/svg/icons/common/Calender";
import useCommonStore from "../../../../stores/common";
import useMerchantStore from "../../../../stores/merchant";
import useDomainStore from "../../../../stores/domain";
import RangeInput from "../../../../components/form/range-input";
import TextInput from "../../../../components/form/text-input";
import Select from "../../../../components/form/select";
import "react-modern-drawer/dist/index.css";
import style from "./style.module.scss";
import useUserStore from "../../../../stores/user";
import { isEntitySuperAdmin } from "../../../../../components/helper/helper";
import { useMediaQuery } from "../../../../hooks/useMediaQuery";
import TagInput from "../../../../components/form/tag-input";
import toast from "react-hot-toast";
import Button from "../../../../components/new-button";

const SAVED_FILTER_PAGE = "merchants";
const MERCHANT_STATUS = [
  { label: "Active", value: "active" },
  { label: "Closed", value: "closed" },
  { label: "Idle", value: "idle" },
];

/**
 * True when the saved snapshot matches the cleared/default merchant filter (nothing to persist).
 */
function isMerchantSavedFilterSnapshotEmpty(snapshot, bounds) {
  if (!bounds?.volume_cap) {
    return false;
  }

  if (snapshot.orgFilter) {
    return false;
  }
  if (snapshot.status && snapshot.status !== "active") {
    return false;
  }
  if (snapshot.dateRangeFrom || snapshot.dateRangeTo) {
    return false;
  }
  if ((snapshot.ems_office?.length ?? 0) > 0) {
    return false;
  }
  if ((snapshot.ems_branch?.length ?? 0) > 0) {
    return false;
  }
  if ((snapshot.rep_codes?.length ?? 0) > 0) {
    return false;
  }
  if (
    snapshot.volumeCapFrom !== bounds.volume_cap.min ||
    snapshot.volumeCapTo !== bounds.volume_cap.max
  ) {
    return false;
  }
  if (
    snapshot.volumeUsedFrom !== bounds.volume_used.min ||
    snapshot.volumeUsedTo !== bounds.volume_used.max
  ) {
    return false;
  }
  if (
    snapshot.volumeMTDFrom !== bounds.volume_mtd.min ||
    snapshot.volumeMTDTo !== bounds.volume_mtd.max
  ) {
    return false;
  }
  if (
    snapshot.momChangeFrom !== bounds.mom_change.min ||
    snapshot.momChangeTo !== bounds.mom_change.max
  ) {
    return false;
  }

  return true;
}

function getAppliedMerchantFilterSnapshot() {
  const { merchantFilterSearchQuery: q } = useMerchantStore.getState();
  const mf = q.merchant_filter;
  const emsOffice = q.ems_office || [];
  const emsBranch = q.ems_branch || [];
  const repCodes = q.rep_codes || [];

  if (!mf) {
    return {
      orgFilter: "",
      status: "active",
      dateRangeFrom: "",
      dateRangeTo: "",
      volumeCapFrom: 0,
      volumeCapTo: 0,
      volumeUsedFrom: 0,
      volumeUsedTo: 0,
      volumeMTDFrom: 0,
      volumeMTDTo: 0,
      momChangeFrom: 0,
      momChangeTo: 0,
      ems_office: [...emsOffice],
      ems_branch: [...emsBranch],
      rep_codes: [...repCodes],
    };
  }

  return {
    orgFilter: mf.org_filter || "",
    status: mf.status || "active",
    dateRangeFrom: mf.date_approved_from || "",
    dateRangeTo: mf.date_approved_to || "",
    volumeCapFrom: mf.volume_from,
    volumeCapTo: mf.volume_to,
    volumeUsedFrom: mf.volume_used_from,
    volumeUsedTo: mf.volume_used_to,
    volumeMTDFrom: mf.volume_mtd_from,
    volumeMTDTo: mf.volume_mtd_to,
    momChangeFrom: mf.mom_change_from,
    momChangeTo: mf.mom_change_to,
    ems_office: [...emsOffice],
    ems_branch: [...emsBranch],
    rep_codes: [...repCodes],
  };
}

function FilterDrawer() {
  const { isKurvDomain } = useDomainStore();
  const isMobile = useMediaQuery(768);
  const isSmallMobile = useMediaQuery(420);
  const filterDrawerOpen = useCommonStore((state) => state.filterDrawerOpen);
  const setFilterDrawerOpen = useCommonStore(
    (state) => state.setFilterDrawerOpen,
  );
  const filterBounds = useMerchantStore((state) => state.filterBounds);
  const setFilterBounds = useMerchantStore((state) => state.setFilterBounds);
  const filterBoundsLoading = useMerchantStore(
    (state) => state.filterBoundsLoading,
  );
  const setFilterBoundsLoading = useMerchantStore(
    (state) => state.setFilterBoundsLoading,
  );
  const setMerchantFilterSearchQuery = useMerchantStore(
    (state) => state.setMerchantFilterSearchQuery,
  );
  const merchantFilterSearchQuery = useMerchantStore(
    (state) => state.merchantFilterSearchQuery,
  );
  const user = useUserStore((state) => state.user);
  const [organizations, setOrganizations] = useState([]);
  const [initialValues, setInitialValues] = useState(null);

  const getFilterSnapshotRef = useRef(() => ({}));
  const applyFilterSnapshotRef = useRef(() => {});

  const savedFiltersApi = useSavedFilters({
    page: SAVED_FILTER_PAGE,
    drawerOpen: filterDrawerOpen,
    getFilterSnapshot: () => getFilterSnapshotRef.current(),
    applyFilterSnapshot: (filters) => applyFilterSnapshotRef.current(filters),
    isSnapshotEmpty: (snapshot) =>
      isMerchantSavedFilterSnapshotEmpty(snapshot, filterBounds),
    getAppliedSnapshot: getAppliedMerchantFilterSnapshot,
  });

  useEffect(() => {
    const abortController = new AbortController();

    // ToDo: Extract this api call and related state to the zustand store.
    getMerchantFilterBounds(merchantFilterSearchQuery, abortController)
      .then((res) => {
        if (res && res?.status && res.data) {
          const newBounds = {};
          Object.keys(res.data).forEach((key) => {
            const bound = res.data[key];
            if (bound.min === bound.max) {
              newBounds[key] = { min: 0, max: Math.max(bound.max, 1) };
            } else {
              newBounds[key] = { min: bound.min, max: bound.max };
            }
          });

          setFilterBounds(newBounds);
          setInitialValues({
            orgFilter: "",
            status: "active",
            dateRangeFrom: "",
            dateRangeTo: "",
            volumeCapFrom: newBounds.volume_cap.min,
            volumeCapTo: newBounds.volume_cap.max,
            volumeUsedFrom: newBounds.volume_used.min,
            volumeUsedTo: newBounds.volume_used.max,
            volumeMTDFrom: newBounds.volume_mtd.min,
            volumeMTDTo: newBounds.volume_mtd.max,
            momChangeFrom: newBounds.mom_change.min,
            momChangeTo: newBounds.mom_change.max,
          });
          setFilterBoundsLoading(false);
        }
      })
      .catch((err) => {
        console.log("Error fetching bounds");
        console.error(err);
        setBoundsLoading(false);
        toast.error("Failed to load bounds");
      });
    if (isEntitySuperAdmin(user)) {
      SearchOrganizations(null)
        .then((res) => {
          if (!res.success) {
            console.error("There was a problem fetching organizations");
          }
          const orgsOptions = res?.data?.map((org) => ({
            label: org.name,
            value: org.id,
          }));
          setOrganizations(orgsOptions);
        })
        .catch((error) => {
          console.error("Error fetching organizations:", error);
          setOrganizations([]);
          toast.error("Failed to load organizations");
        });
    }

    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClear = (resetForm) => {
    const clearedValues = {
      orgFilter: "",
      status: "active",
      dateRangeFrom: "",
      dateRangeTo: "",
      volumeCapFrom: filterBounds.volume_cap.min,
      volumeCapTo: filterBounds.volume_cap.max,
      volumeUsedFrom: filterBounds.volume_used.min,
      volumeUsedTo: filterBounds.volume_used.max,
      volumeMTDFrom: filterBounds.volume_mtd.min,
      volumeMTDTo: filterBounds.volume_mtd.max,
      momChangeFrom: filterBounds.mom_change.min,
      momChangeTo: filterBounds.mom_change.max,
    };

    resetForm({ values: clearedValues });
    setSelectedEmsOffices([]);
    setSelectedEmsBranches([]);
    setSelectedRepCodes([]);
    setMerchantFilterSearchQuery({
      ems_office: [],
      ems_branch: [],
      rep_codes: [],
      merchant_filter: {
        org_filter: "",
        status: "active",
        search: "",
        date_approved_from: "",
        date_approved_to: "",

        volume_from: filterBounds.volume_cap.min,
        volume_to: filterBounds.volume_cap.max,

        volume_used_from: filterBounds.volume_used.min,
        volume_used_to: filterBounds.volume_used.max,

        volume_mtd_from: filterBounds.volume_mtd.min,
        volume_mtd_to: filterBounds.volume_mtd.max,

        mom_change_from: filterBounds.mom_change.min,
        mom_change_to: filterBounds.mom_change.max,
      },
    });
    setFilterDrawerOpen(false);
  };

  const onApplyClick = (values) => {
    setMerchantFilterSearchQuery({
      ems_office: selectedEmsOffices,
      ems_branch: selectedEmsBranches,
      rep_codes: selectedRepCodes,
      merchant_filter: {
        org_filter: values.orgFilter,
        volume_from: values.volumeCapFrom,
        volume_to: values.volumeCapTo,
        volume_used_from: values.volumeUsedFrom,
        volume_used_to: values.volumeUsedTo,
        volume_mtd_from: values.volumeMTDFrom,
        volume_mtd_to: values.volumeMTDTo,
        mom_change_from: values.momChangeFrom,
        mom_change_to: values.momChangeTo,
        date_approved_from: values.dateRangeFrom,
        date_approved_to: values.dateRangeTo,
        status: values.status,
      },
    });

    savedFiltersApi.commitAppliedSavedFilter();
    setFilterDrawerOpen(false);
  };

  const getActiveFilters = (values) => {
    const activeFilters = [];

    // Status filter
    if (values.status && values.status !== "active") {
      const statusValue = String(values.status);
      activeFilters.push({
        type: "status",
        value: statusValue.charAt(0).toUpperCase() + statusValue.slice(1),
        id: "status",
      });
    }

    // Volume Cap filter
    if (
      values?.volumeCapFrom !== filterBounds.volume_cap.min ||
      values?.volumeCapTo !== filterBounds.volume_cap.max
    ) {
      activeFilters.push({
        type: "volumeCap",
        value: `Volume Cap: $${standardCurrencyFormatter(values.volumeCapFrom)} - $${standardCurrencyFormatter(values.volumeCapTo)}`,
        id: "volumeCap",
      });
    }

    // Volume Used filter
    if (
      values?.volumeUsedFrom !== filterBounds.volume_used.min ||
      values.volumeUsedTo !== filterBounds.volume_used.max
    ) {
      activeFilters.push({
        type: "volumeUsed",
        value: `Volume Used: ${standardCurrencyFormatter(values.volumeUsedFrom)}% - ${standardCurrencyFormatter(values.volumeUsedTo)}%`,
        id: "volumeUsed",
      });
    }

    // Volume MTD filter
    if (
      values?.volumeMTDFrom !== filterBounds.volume_mtd.min ||
      values?.volumeMTDTo !== filterBounds.volume_mtd.max
    ) {
      activeFilters.push({
        type: "volumeMTD",
        value: `Volume MTD: $${standardCurrencyFormatter(values.volumeMTDFrom)} - $${standardCurrencyFormatter(values.volumeMTDTo)}`,
        id: "volumeMTD",
      });
    }

    // MOM Change filter
    if (
      values?.momChangeFrom !== filterBounds.mom_change.min ||
      values?.momChangeTo !== filterBounds.mom_change.max
    ) {
      activeFilters.push({
        type: "momChange",
        value: `MOM Change: ${standardCurrencyFormatter(values.momChangeFrom)}% - ${standardCurrencyFormatter(values.momChangeTo)}%`,
        id: "momChange",
      });
    }

    // Date Range filter
    if (values?.dateRangeFrom || values?.dateRangeTo) {
      const fromDate = values.dateRangeFrom || "Start";
      const toDate = values?.dateRangeTo || "End";
      activeFilters.push({
        type: "dateRange",
        value: `Date: ${fromDate} - ${toDate}`,
        id: "dateRange",
      });
    }

    return activeFilters;
  };

  const removeFilter = (filter, setFieldValue) => {
    switch (filter.type) {
      case "status":
        setFieldValue("status", "active");
        break;
      case "volumeCap":
        setFieldValue("volumeCapFrom", filterBounds.volume_cap.min);
        setFieldValue("volumeCapTo", filterBounds.volume_cap.max);
        break;
      case "volumeUsed":
        setFieldValue("volumeUsedFrom", filterBounds.volume_used.min);
        setFieldValue("volumeUsedTo", filterBounds.volume_used.max);
        break;
      case "volumeMTD":
        setFieldValue("volumeMTDFrom", filterBounds.volume_mtd.min);
        setFieldValue("volumeMTDTo", filterBounds.volume_mtd.max);
        break;
      case "momChange":
        setFieldValue("momChangeFrom", filterBounds.mom_change.min);
        setFieldValue("momChangeTo", filterBounds.mom_change.max);
        break;
      case "dateRange":
        setFieldValue("dateRangeFrom", "");
        setFieldValue("dateRangeTo", "");
        break;
      default:
        break;
    }
  };
  const handleOnSearchInputChange = async (term) => {
    try {
      const res = await SearchOrganizations({ term });
      if (!res.success) {
        console.error("There was a problem fetching organizations");
      }

      const orgsOptions = res?.data.map((org) => ({
        label: org.name,
        value: org.id,
      }));

      setOrganizations(orgsOptions);
    } catch (error) {
      console.error("Error fetching organizations:", error);
    }
  };

  const handleOnOrganizationSelectChange = (selectedOption, setFieldValue) => {
    const value =
      typeof selectedOption === "object"
        ? selectedOption.value
        : selectedOption;
    setFieldValue("orgFilter", value);
  };

  const handleOnStatusSelectChange = (selectedOption, setFieldValue) => {
    const value =
      typeof selectedOption === "object"
        ? selectedOption.value
        : selectedOption;
    setFieldValue("status", value);
  };

  const getCurrentFilterValues = (values) => ({
    orgFilter: values.orgFilter,
    status: values.status,
    dateRangeFrom: values.dateRangeFrom,
    dateRangeTo: values.dateRangeTo,
    volumeCapFrom: values.volumeCapFrom,
    volumeCapTo: values.volumeCapTo,
    volumeUsedFrom: values.volumeUsedFrom,
    volumeUsedTo: values.volumeUsedTo,
    volumeMTDFrom: values.volumeMTDFrom,
    volumeMTDTo: values.volumeMTDTo,
    momChangeFrom: values.momChangeFrom,
    momChangeTo: values.momChangeTo,
    ems_office: selectedEmsOffices,
    ems_branch: selectedEmsBranches,
    rep_codes: selectedRepCodes,
  });

  const applyFilterValues = (filters, setFieldValue) => {
    setFieldValue("orgFilter", filters.orgFilter || "");
    setFieldValue("status", filters.status || "active");
    setFieldValue("dateRangeFrom", filters.dateRangeFrom || "");
    setFieldValue("dateRangeTo", filters.dateRangeTo || "");
    setFieldValue("volumeCapFrom", filters.volumeCapFrom ?? filterBounds.volume_cap.min);
    setFieldValue("volumeCapTo", filters.volumeCapTo ?? filterBounds.volume_cap.max);
    setFieldValue("volumeUsedFrom", filters.volumeUsedFrom ?? filterBounds.volume_used.min);
    setFieldValue("volumeUsedTo", filters.volumeUsedTo ?? filterBounds.volume_used.max);
    setFieldValue("volumeMTDFrom", filters.volumeMTDFrom ?? filterBounds.volume_mtd.min);
    setFieldValue("volumeMTDTo", filters.volumeMTDTo ?? filterBounds.volume_mtd.max);
    setFieldValue("momChangeFrom", filters.momChangeFrom ?? filterBounds.mom_change.min);
    setFieldValue("momChangeTo", filters.momChangeTo ?? filterBounds.mom_change.max);
    setSelectedEmsOffices(filters.ems_office || []);
    setSelectedEmsBranches(filters.ems_branch || []);
    setSelectedRepCodes(filters.rep_codes || []);
  };

  useEffect(() => {
    fetchEmsBranches();
    fetchRepCodes();
  }, []);

  const [selectedEmsOffices, setSelectedEmsOffices] = useState([]);
  const {
    options: emsOffices,
    hasMore: hasMoreEmsOffices,
    loading: isLoadingEmsOffices,
    loadingMore: isLoadingMoreEmsOffices,
    search: searchEmsOffices,
    loadMore: loadMoreEmsOffices,
  } = usePaginatedOfficeOptions(getEmsOffices, { lazy: true });

  useEffect(() => {
    if (filterDrawerOpen) searchEmsOffices("");
  }, [filterDrawerOpen]);

  // EMS Branch filter
  const [emsBranches, setEmsBranches] = useState([]);
  const [selectedEmsBranches, setSelectedEmsBranches] = useState([]);
  const [isLoadingEmsBranches, setIsLoadingEmsBranches] = useState(false);

  /**
   * Fetches representative codes from the API
   * @async
   * @returns {Promise<void>}
   */
  const fetchEmsBranches = async () => {
    try {
      setIsLoadingEmsBranches(true);
      const response = await getUserBranchesList();
      if (response.status) {
        setEmsBranches(response.data);
      }
    } catch (error) {
      setEmsBranches([]);
    } finally {
      setIsLoadingEmsBranches(false);
    }
  };

  // Rep Code filter
  const [repCodes, setRepCodes] = useState([]);
  const [selectedRepCodes, setSelectedRepCodes] = useState([]);
  const [isLoadingRepCodes, setIsLoadingRepCodes] = useState(false);

  /**
   * Fetches representative codes from the API
   * @async
   * @returns {Promise<void>}
   */
  const fetchRepCodes = async () => {
    try {
      setIsLoadingRepCodes(true);
      const response = await getEmsRepCodes();

      if (Array.isArray(response)) {
        setRepCodes(response);
      } else if (response.data && Array.isArray(response.data)) {
        setRepCodes(response.data);
      } else {
        setRepCodes([]);
      }
    } catch (error) {
      setRepCodes([]);
    } finally {
      setIsLoadingRepCodes(false);
    }
  };

  return (
    <Drawer
      open={filterDrawerOpen}
      direction={isMobile ? "left" : "right"}
      className={`${style.filter_drawer} ${isKurvDomain ? style.kurv_theme : ""}`}
      size={isSmallMobile ? "320px" : isMobile ? "375px" : "440px"}
      zIndex={8888}
      overlayColor={"#000"}
      overlayOpacity={0.5}
      onClose={() => {
        setFilterDrawerOpen(false);
      }}
    >
      {initialValues && (
        <Formik
          initialValues={initialValues}
          validationSchema={filterSchema}
          validateOnBlur
          validateOnChange
          onSubmit={onApplyClick}
        >
          {({
            values,
            errors,
            touched,
            handleSubmit,
            isValid,
            setFieldValue,
            handleBlur,
            handleChange,
            resetForm,
          }) => {
            getFilterSnapshotRef.current = () => getCurrentFilterValues(values);
            applyFilterSnapshotRef.current = (filters) =>
              applyFilterValues(filters, setFieldValue);

            return (
 <>
              <div className={style.drawer_container}>
                <div className={style.drawer_title_block}>
                  <div className={style.head}>
                    <ButtonIcon onClick={() => setFilterDrawerOpen(false)}>
                      <CloseIcon24 />
                    </ButtonIcon>
                  </div>

                  <div className={style.header}>
                    <h5>Filter</h5>
                    {getActiveFilters(values).length > 0 && (
                      <div className={style.active_filters}>
                        {getActiveFilters(values).map((filter, index) => (
                          <div key={index} className={style.filter_pill}>
                            {filter.value}
                            <button
                              onClick={() => removeFilter(filter, setFieldValue)}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className={style.content}>
                  <SavedFiltersSection
                    savedFilterOptions={savedFiltersApi.savedFilterOptions}
                    selectedSavedFilter={savedFiltersApi.selectedSavedFilter}
                    onSavedFilterChange={
                      savedFiltersApi.handleSavedFilterSelect
                    }
                    onDeleteSavedFilter={savedFiltersApi.handleDeleteSavedFilter}
                    isDeletingFilter={savedFiltersApi.isDeletingFilter}
                    editFilterName={savedFiltersApi.editFilterName}
                    onEditFilterNameChange={savedFiltersApi.setEditFilterName}
                    onUpdateSavedFilter={savedFiltersApi.handleUpdateSavedFilter}
                    isUpdatingFilter={savedFiltersApi.isUpdatingFilter}
                    showSaveFilterInput={savedFiltersApi.showSaveFilterInput}
                    onShowSaveInput={savedFiltersApi.openSaveFilterInput}
                    newFilterName={savedFiltersApi.newFilterName}
                    onNewFilterNameChange={savedFiltersApi.setNewFilterName}
                    onSaveFilter={savedFiltersApi.handleSaveFilter}
                    isSavingFilter={savedFiltersApi.isSavingFilter}
                    onCancelNewFilter={() => {
                      savedFiltersApi.cancelSaveFilterInput();
                      savedFiltersApi.setNewFilterName("");
                    }}
                  />

                  {isEntitySuperAdmin(user) && (
                    <div className={style.organization_section}>
                      <h6 className={style.title}>Organization</h6>
                      <div className={style.organization_container}>
                        <Select
                          async
                          search
                          options={organizations}
                          value={values.orgFilter}
                          onSearchChange={handleOnSearchInputChange}
                          onChange={(selectedOption) =>
                            handleOnOrganizationSelectChange(
                              selectedOption,
                              setFieldValue,
                            )
                          }
                          placeholder="Search organizations"
                        />
                      </div>
                    </div>
                  )}

                  <div
                    className={`${style.field_container} ${filterBoundsLoading ? style.loading : ""}`}
                  >
                    <div className={style.field_header}>
                      <h6>Volume Cap</h6>
                    </div>

                    <div
                      className={`${style.field_body} ${style.flexDirectionColumn} ${style.noGap}`}
                    >
                      <div className={style.formGroupRow}>
                        <div className={style.field_input}>
                          <TextInput
                            type="text"
                            name="volumeCapFrom"
                            label="Volume Cap from"
                            placeholder="0.0"
                            value={standardCurrencyFormatter(
                              values.volumeCapFrom,
                            )}
                            disabled={filterBoundsLoading}
                            prefix="$"
                            errored={
                              !!errors.volumeCapFrom && touched.volumeCapFrom
                            }
                            error={errors.volumeCapFrom}
                            touched={touched.volumeCapFrom}
                            onBlur={handleBlur}
                            cleaveOptions={{
                              numeral: true,
                              numeralDecimalMark: ".",
                              numeralDecimalScale: 2,
                              numeralThousandsGroupStyle: "thousand",
                              numeralPositiveOnly: true,
                              onValueChanged: (e) => {
                                setFieldValue(
                                  "volumeCapFrom",
                                  parseFloat(e.target.rawValue) || 0,
                                );
                              },
                            }}
                          />
                        </div>

                        <div className={style.field_input}>
                          <TextInput
                            type="text"
                            name="volumeCapTo"
                            label="Volume Cap to"
                            placeholder="0.0"
                            value={standardCurrencyFormatter(
                              values.volumeCapTo,
                            )}
                            disabled={filterBoundsLoading}
                            prefix="$"
                            errored={
                              !!errors.volumeCapTo && touched.volumeCapTo
                            }
                            error={errors.volumeCapTo}
                            touched={touched.volumeCapTo}
                            onBlur={handleBlur}
                            cleaveOptions={{
                              numeral: true,
                              numeralDecimalMark: ".",
                              numeralDecimalScale: 2,
                              numeralThousandsGroupStyle: "thousand",
                              numeralPositiveOnly: true,
                              onValueChanged: (e) => {
                                setFieldValue(
                                  "volumeCapTo",
                                  parseFloat(e.target.rawValue) || 0,
                                );
                              },
                            }}
                          />
                        </div>
                      </div>

                      <div
                        className={
                          filterBoundsLoading ? style.range_input_disabled : ""
                        }
                        style={{ width: "100%" }}
                      >
                        <RangeInput
                          disabled={filterBoundsLoading}
                          min={filterBounds.volume_cap.min}
                          max={filterBounds.volume_cap.max}
                          value={[values.volumeCapFrom, values.volumeCapTo]}
                          onChange={(value) => {
                            setFieldValue("volumeCapFrom", value[0]);
                            setFieldValue("volumeCapTo", value[1]);
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div
                    className={`${style.field_container} ${filterBoundsLoading ? style.loading : ""}`}
                  >
                    <div className={style.field_header}>
                      <h6>Volume Used</h6>
                    </div>
                    <div
                      className={`${style.field_body} ${style.flexDirectionColumn} ${style.noGap}`}
                    >
                      <div className={style.formGroupRow}>
                        <div className={style.field_input}>
                          <TextInput
                            type="text"
                            name="volumeUsedFrom"
                            label="Volume used from"
                            placeholder="0.0"
                            value={standardCurrencyFormatter(
                              values.volumeUsedFrom,
                            )}
                            disabled={filterBoundsLoading}
                            suffix="%"
                            errored={
                              !!errors.volumeUsedFrom && touched.volumeUsedFrom
                            }
                            error={errors.volumeUsedFrom}
                            touched={touched.volumeUsedFrom}
                            onBlur={handleBlur}
                            cleaveOptions={{
                              numeral: true,
                              numeralDecimalMark: ".",
                              numeralDecimalScale: 2,
                              numeralThousandsGroupStyle: "thousand",
                              numeralPositiveOnly: true,
                              onValueChanged: (e) => {
                                setFieldValue(
                                  "volumeUsedFrom",
                                  parseFloat(e.target.rawValue) || 0,
                                );
                              },
                            }}
                          />
                        </div>

                        <div className={style.field_input}>
                          <TextInput
                            type="text"
                            name="volumeUsedTo"
                            label="Volume used to"
                            placeholder="0.0"
                            value={standardCurrencyFormatter(
                              values.volumeUsedTo,
                            )}
                            disabled={filterBoundsLoading}
                            suffix="%"
                            errored={
                              !!errors.volumeUsedTo && touched.volumeUsedTo
                            }
                            error={errors.volumeUsedTo}
                            touched={touched.volumeUsedTo}
                            onBlur={handleBlur}
                            cleaveOptions={{
                              numeral: true,
                              numeralDecimalMark: ".",
                              numeralDecimalScale: 2,
                              numeralThousandsGroupStyle: "thousand",
                              numeralPositiveOnly: true,
                              onValueChanged: (e) => {
                                setFieldValue(
                                  "volumeUsedTo",
                                  parseFloat(e.target.rawValue) || 0,
                                );
                              },
                            }}
                          />
                        </div>
                      </div>

                      <div
                        className={
                          filterBoundsLoading ? style.range_input_disabled : ""
                        }
                        style={{ width: "100%" }}
                      >
                        <RangeInput
                          disabled={filterBoundsLoading}
                          min={filterBounds.volume_used.min}
                          max={filterBounds.volume_used.max}
                          value={[values.volumeUsedFrom, values.volumeUsedTo]}
                          onChange={(value) => {
                            setFieldValue("volumeUsedFrom", value[0]);
                            setFieldValue("volumeUsedTo", value[1]);
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div
                    className={`${style.field_container} ${filterBoundsLoading ? style.loading : ""}`}
                  >
                    <div className={style.field_header}>
                      <h6>Volume MTD</h6>
                    </div>
                    <div
                      className={`${style.field_body} ${style.flexDirectionColumn} ${style.noGap}`}
                    >
                      <div className={style.formGroupRow}>
                        <div className={style.field_input}>
                          <TextInput
                            type="text"
                            name="volumeMTDFrom"
                            label="Volume MTD from"
                            placeholder="0.0"
                            value={standardCurrencyFormatter(
                              values.volumeMTDFrom,
                            )}
                            disabled={filterBoundsLoading}
                            prefix="$"
                            errored={
                              !!errors.volumeMTDFrom && touched.volumeMTDFrom
                            }
                            error={errors.volumeMTDFrom}
                            touched={touched.volumeMTDFrom}
                            onBlur={handleBlur}
                            cleaveOptions={{
                              numeral: true,
                              numeralDecimalMark: ".",
                              numeralDecimalScale: 2,
                              numeralThousandsGroupStyle: "thousand",
                              numeralPositiveOnly: true,
                              onValueChanged: (e) => {
                                setFieldValue(
                                  "volumeMTDFrom",
                                  parseFloat(e.target.rawValue) || 0,
                                );
                              },
                            }}
                          />
                        </div>

                        <div className={style.field_input}>
                          <TextInput
                            type="text"
                            name="volumeMTDTo"
                            label="Volume MTD to"
                            placeholder="0.0"
                            value={standardCurrencyFormatter(
                              values.volumeMTDTo,
                            )}
                            disabled={filterBoundsLoading}
                            prefix="$"
                            errored={
                              !!errors.volumeMTDTo && touched.volumeMTDTo
                            }
                            error={errors.volumeMTDTo}
                            touched={touched.volumeMTDTo}
                            onBlur={handleBlur}
                            cleaveOptions={{
                              numeral: true,
                              numeralDecimalMark: ".",
                              numeralDecimalScale: 2,
                              numeralThousandsGroupStyle: "thousand",
                              numeralPositiveOnly: true,
                              onValueChanged: (e) => {
                                setFieldValue(
                                  "volumeMTDTo",
                                  parseFloat(e.target.rawValue) || 0,
                                );
                              },
                            }}
                          />
                        </div>
                      </div>

                      <div
                        className={
                          filterBoundsLoading ? style.range_input_disabled : ""
                        }
                        style={{ width: "100%" }}
                      >
                        <RangeInput
                          disabled={filterBoundsLoading}
                          min={filterBounds.volume_mtd.min}
                          max={filterBounds.volume_mtd.max}
                          value={[values.volumeMTDFrom, values.volumeMTDTo]}
                          onChange={(value) => {
                            setFieldValue("volumeMTDFrom", value[0]);
                            setFieldValue("volumeMTDTo", value[1]);
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div
                    className={`${style.field_container} ${filterBoundsLoading ? style.loading : ""}`}
                  >
                    <div className={style.field_header}>
                      <h6>MOM Change</h6>
                    </div>
                    <div
                      className={`${style.field_body} ${style.flexDirectionColumn} ${style.noGap}`}
                    >
                      <div className={style.formGroupRow}>
                        <div className={style.field_input}>
                          <TextInput
                            type="text"
                            label="MOM change from"
                            placeholder="0.0"
                            value={standardCurrencyFormatter(
                              values.momChangeFrom,
                            )}
                            disabled={filterBoundsLoading}
                            suffix="%"
                            errored={
                              !!errors.momChangeFrom && touched.momChangeFrom
                            }
                            error={errors.momChangeFrom}
                            touched={touched.momChangeFrom}
                            onBlur={handleBlur}
                            cleaveOptions={{
                              numeral: true,
                              numeralDecimalMark: ".",
                              numeralDecimalScale: 2,
                              numeralThousandsGroupStyle: "thousand",
                              numeralPositiveOnly: true,
                              onValueChanged: (e) => {
                                setFieldValue(
                                  "momChangeFrom",
                                  parseFloat(e.target.rawValue) || 0,
                                );
                              },
                            }}
                          />
                        </div>

                        <div className={style.field_input}>
                          <TextInput
                            type="text"
                            label="MOM change to"
                            placeholder="0.0"
                            value={standardCurrencyFormatter(
                              values.momChangeTo,
                            )}
                            disabled={filterBoundsLoading}
                            suffix="%"
                            errored={
                              !!errors.momChangeTo && touched.momChangeTo
                            }
                            error={errors.momChangeTo}
                            touched={touched.momChangeTo}
                            onBlur={handleBlur}
                            cleaveOptions={{
                              numeral: true,
                              numeralDecimalMark: ".",
                              numeralDecimalScale: 2,
                              numeralThousandsGroupStyle: "thousand",
                              numeralPositiveOnly: true,
                              onValueChanged: (e) => {
                                setFieldValue(
                                  "momChangeTo",
                                  parseFloat(e.target.rawValue) || 0,
                                );
                              },
                            }}
                          />
                        </div>
                      </div>

                      <div
                        className={
                          filterBoundsLoading ? style.range_input_disabled : ""
                        }
                        style={{ width: "100%" }}
                      >
                        <RangeInput
                          disabled={filterBoundsLoading}
                          min={filterBounds.mom_change.min}
                          max={filterBounds.mom_change.max}
                          step={1}
                          value={[values.momChangeFrom, values.momChangeTo]}
                          onChange={(value) => {
                            setFieldValue("momChangeFrom", value[0]);
                            setFieldValue("momChangeTo", value[1]);
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className={style.field_container}>
                    <div className={style.field_header}>
                      <h6>Date approved</h6>
                    </div>
                    <div
                      className={`${style.field_body} ${style.flexDirectionColumn} ${style.noGap}`}
                    >
                      <div className={style.formGroupRow}>
                        <div className={style.field_input}>
                          <TextInput
                            label="Date approved from"
                            type="date"
                            name="dateRangeFrom"
                            value={values.dateRangeFrom}
                            errored={
                              !!errors.dateRangeFrom && touched.dateRangeFrom
                            }
                            error={errors.dateRangeFrom}
                            touched={touched.dateRangeFrom}
                            onBlur={handleBlur}
                            onChange={(e) =>
                              setFieldValue("dateRangeFrom", e.target.value)
                            }
                          />
                        </div>
                        <div className={style.field_input}>
                          <TextInput
                            label="Date approved to"
                            type="date"
                            name="dateRangeTo"
                            value={values.dateRangeTo}
                            errored={!!errors.dateRangeTo && touched.dateRangeTo}
                            error={errors.dateRangeTo}
                            touched={touched.dateRangeTo}
                            onBlur={handleBlur}
                            onChange={(e) =>
                              setFieldValue("dateRangeTo", e.target.value)
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={style.field_container}>
                    <div className={style.field_header}>
                      <h6>KURV OFFICE</h6>
                    </div>
                    <div className={style.field_body}>
                      <div
                        className={style.formGroupRow}
                        style={{ width: "100%" }}
                      >
                        <TagInput
                          id={"ems_office"}
                          name={"ems_office"}
                          options={emsOffices}
                          // office_id is its own label here, so no lookup needed.
                          value={selectedEmsOffices.map((id) => ({
                            label: id,
                            value: id,
                          }))}
                          onChange={(value) => {
                            const newEmsOffices = value.map(
                              (option) => option.value,
                            );
                            setSelectedEmsOffices(newEmsOffices);
                          }}
                          placeholder={"Select Kurv Office"}
                          search={true}
                          async
                          serverFiltered
                          loading={isLoadingEmsOffices}
                          onSearchChange={searchEmsOffices}
                          hasMore={hasMoreEmsOffices}
                          loadingMore={isLoadingMoreEmsOffices}
                          onLoadMore={loadMoreEmsOffices}
                          className={style.tag_input}
                        />
                      </div>
                    </div>
                  </div>

                  <div className={style.field_container}>
                    <div className={style.field_header}>
                      <h6>KURV BRANCH</h6>
                    </div>
                    <div className={style.field_body}>
                      <div
                        className={style.formGroupRow}
                        style={{ width: "100%" }}
                      >
                        <TagInput
                          id={"ems_branch"}
                          name={"ems_branch"}
                          options={emsBranches}
                          value={emsBranches?.filter((option) =>
                            selectedEmsBranches.includes(option.value),
                          )}
                          onChange={(value) => {
                            const newEmsBranches = value.map(
                              (option) => option.value,
                            );
                            setSelectedEmsBranches(newEmsBranches);
                          }}
                          placeholder={"Select Kurv Branch"}
                          search={true}
                          className={style.tag_input}
                        />
                      </div>
                    </div>
                  </div>

                  <div className={style.field_container}>
                    <div className={style.field_header}>
                      <h6>KURV REP CODE</h6>
                    </div>
                    <div className={style.field_body}>
                      <div
                        className={style.formGroupRow}
                        style={{ width: "100%" }}
                      >
                        <TagInput
                          id={"ems_rep_code"}
                          name={"ems_rep_code"}
                          options={repCodes}
                          value={repCodes?.filter((option) =>
                            selectedRepCodes.includes(option.value),
                          )}
                          onChange={(value) => {
                            const newRepCodes = value.map(
                              (option) => option.value,
                            );
                            setSelectedRepCodes(newRepCodes);
                          }}
                          placeholder={"Select Kurv Rep Code"}
                          search={true}
                          className={style.tag_input}
                        />
                      </div>
                    </div>
                  </div>

                  <div className={style.organization_section}>
                    <h6 className={style.title}>Status</h6>
                    <div className={style.organization_container}>
                      <Select
                        id={"status"}
                        name={"status"}
                        options={MERCHANT_STATUS}
                        value={values.status}
                        onChange={(selectedOption) =>
                          handleOnStatusSelectChange(
                            selectedOption,
                            setFieldValue,
                          )
                        }
                        placeholder="Select Status"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className={style.footer}>
                <Button
                  variant="secondary"
                  size="large"
                  onClick={() => handleClear(resetForm)}
                >
                  Clear
                </Button>
                <Button variant="primary" size="large" onClick={handleSubmit}>
                  Apply
                </Button>
              </div>
            </>
            );
          }}
        </Formik>
      )}
    </Drawer>
  );
}

export default FilterDrawer;
